<template>
  <div ref="slider" class="airsage-range-slider mb-3">
    <div class="flex pb-2">
      <div class="capitalize">{{ label }}:&nbsp;</div>
      <div>{{ minValue }}{{ unit }}</div>
      <div>&nbsp;-&nbsp;</div>
      <div>{{ maxValue }}{{ unit }}</div>
    </div>
    <div>
      <input class="min" type="range" name="min" id="min" :min="min" :max="max" :value="minValue" :step="step"
        @input="({ target }) => (minValue = parseFloat(target.value))" />
      <input class="max" type="range" name="max" id="max" :min="min" :max="max" :value="maxValue" :step="step"
        @input="({ target }) => (maxValue = parseFloat(target.value))" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";

const props = defineProps({
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  minValue: {
    type: Number,
    default: 50,
  },
  maxValue: {
    type: Number,
    default: 80,
  },
  label: String,
  unit: {
    type: String,
    default: ""
  },
  step: {
    type: Number,
    default: 1
  }
});

// define emits for the slider component
const emit = defineEmits(["update:minValue", "update:maxValue"]);

const slider = ref(null);

const minValue = computed({
  get() {
    return props.minValue;
  },
  set(value) {
    emit("update:minValue", value);
  }
});

const maxValue = computed({
  get() {
    return props.maxValue;
  },
  set(value) {
    emit("update:maxValue", value);
  }
});

const getPercent = (value, min, max) => {
  return ((value - min) / (max - min)) * 100;
};

const setCSSProps = () => {
  if (slider.value) {
    slider.value.style.setProperty("--progressLeft", `${getPercent(minValue.value, props.min, props.max)}%`);
    slider.value.style.setProperty("--progressRight", `${100 - getPercent(maxValue.value, props.min, props.max)}%`);
  }
};

watch([minValue, maxValue], setCSSProps);
onMounted(setCSSProps);
</script>

<style scoped>
.airsage-range-slider {
  --trackHeight: 0.5rem;
  --thumbRadius: 1rem;
  position: relative;
}

/* style the input element with type "range" */
.airsage-range-slider input[type="range"] {
  position: absolute;
  appearance: none;
  width: 100%;
  border-radius: 999px;
  background: transparent;
}

/* ::before element to replace the slider track */
.airsage-range-slider input[type="range"]::before {
  content: "";
  position: absolute;
  left: var(--progressLeft);
  right: var(--progressRight);
  height: 100%;
  background: #1675D1;
  pointer-events: none;
  border-radius: 999px;
}

.airsage-range-slider input[type="range"]::-webkit-slider-runnable-track {
  appearance: none;
  background: #2F3B44;
  height: var(--trackHeight);
  border-radius: 999px;
}

.airsage-range-slider input[type="range"]::-moz-range-track {
  appearance: none;
  background: #2F3B44;
  height: var(--trackHeight);
  border-radius: 999px;
}

.airsage-range-slider input[type="range"]::-webkit-slider-thumb {
  position: relative;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  background: #1675D1;
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  cursor: pointer;
  z-index: 1;
}

.airsage-range-slider input[type="range"]::-moz-range-thumb {
  position: relative;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  background: #1675D1;
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  cursor: pointer;
  z-index: 1;
}
</style>